import { mergeWith } from 'lodash';
import {
  assigned,
  courseGradeColumns,
  flags,
  fpInstructScoreColumns,
  fpRelScoreColumns,
  highPriorityCourseGradeColumns,
  iReadyColumns,
  priorityColumns,
  stateExamColumns,
  subjectSupportColumns,
} from './academic-mad-lib-columns.constant';
import { IAcademicDimensionCol, IAcademicDimensionOp } from './academic-mad-lib.interface';

export class RiskGroupOptions {
  private static options = [
    {
      human: 'School risk group',
      key: 'SCHOOL_RISK_GROUP',
      columns: {} as IAcademicDimensionCol,
      dimension: 'Grouping',
    },
    {
      human: 'DOE risk group',
      key: 'DOE_RISK_GROUP',
      columns: {} as IAcademicDimensionCol,
      dimension: 'Grouping',
    },
  ];
  static getEla(): Array<Partial<IAcademicDimensionOp>> {
    return this.options.map((option: Partial<IAcademicDimensionOp>) => {
      return mergeWith(
        { human: `${option.human} (ELA)`, validString: `${option.key}_ELA` },
        option,
        (objVal, srcVal) => {
          return !objVal ? srcVal : objVal;
        },
      );
    });
  }
  static getMath(): Array<Partial<IAcademicDimensionOp>> {
    return this.options.map((option: Partial<IAcademicDimensionOp>) => {
      return mergeWith(
        { human: `${option.human} (Math)`, validString: `${option.key}_MATH` },
        option,
        (objVal, srcVal) => {
          return !objVal ? srcVal : objVal;
        },
      );
    });
  }
}

export const progressOptions = [
  {
    human: 'Need',
    key: 'PROGRESS',
    columns: {},
    dimension: 'Grouping',
  },
];

export const subjectFailureOptions = [
  {
    human: 'Priority',
    key: 'PRIORITY',
    columns: priorityColumns,
    dimension: 'Grouping',
  },
  {
    human: 'Support',
    key: 'SUBJECT_SUPPORT',
    columns: subjectSupportColumns,
    dimension: 'Grouping',
  },
];

export const supportOptions = [
  {
    human: 'Support',
    key: 'SUPPORT',
    columns: { 30: assigned },
    dimension: 'Grouping',
  },
];

export const flagOptions = [
  {
    human: 'Flag type',
    key: 'FLAG',
    columns: { 40: flags },
  },
];

export const iReadyOptions = [
  {
    human: 'ELA iReady',
    key: 'ELA_IREADY',
    columns: iReadyColumns,
    dimension: 2,
  },
  {
    human: 'Math iReady',
    key: 'MATH_IREADY',
    columns: iReadyColumns,
    dimension: 2,
  },
];

export const stateExamOptions = [
  {
    human: 'State Exam',
    key: 'STATE_EXAM',
    columns: stateExamColumns,
    dimension: 2,
  },
];

export const fpOptions = [
  {
    human: 'F&P ELA',
    key: 'F_&_P_ELA',
    columns: {},
    dimension: 2,
  },
];

export const fpOptionsRelScore = [
  {
    human: 'Relative score',
    key: 'F_&_P_ELA_REL_SCORE',
    columns: fpRelScoreColumns,
    dimension: 'Grouping',
  },
];

export const fpOptionsInstructScore = [
  {
    human: 'Instructional score',
    key: 'F_&_P_ELA_INSTRUC_SCORE',
    columns: fpInstructScoreColumns,
    dimension: 'Grouping',
  },
];

export const fpOptionsSupport = [
  {
    human: 'Support',
    key: 'F_&_P_ELA_SUPPORT',
    columns: fpInstructScoreColumns,
    dimension: 'Grouping',
  },
];

export const courseOptions = [
  {
    human: 'Course summary',
    key: 'COURSE_GRADES',
    columns: courseGradeColumns,
    dimension: 2,
  },
  {
    human: 'High priority course summary',
    key: 'HIGH_PRIORITY_COURSE_GRADES',
    columns: highPriorityCourseGradeColumns,
    dimension: 2,
  },
];

// es & ms subject options
export const esAndMsSubjectOptions = [
  {
    human: 'ELA Failures',
    key: 'ELA_CORE_MS',
    subject: 'ELA',
    columns: {},
    dimension: 2,
  },
  {
    human: 'Math Failures',
    key: 'MATH_CORE_MS',
    subject: 'math',
    columns: {},
    dimension: 2,
  },
  {
    human: 'Sci Failures',
    key: 'SCI_CORE_MS',
    subject: 'sci',
    columns: {},
    dimension: 2,
  },
  {
    human: 'SS Failures',
    key: 'SS_CORE_MS',
    subject: 'SS',
    columns: {},
    dimension: 2,
  },
];

/**
 * HS Subject Options
 * These are currently under-construction & the keys have no been decided
 * Will touch base with Glen next week on these
 */
export const hsSubjectOptions = [
  {
    human: 'ELA Core Failures',
    key: 'ELA_CORE',
    subject: 'ELA',
    columns: {},
    dimension: 2,
  },
  {
    human: 'Math/Alg Failures',
    key: 'MATH_ALG',
    subject: 'math/alg',
    columns: {},
    dimension: 2,
  },
  {
    human: 'Math/Geom Failures',
    key: 'MATH_GEOM',
    subject: 'math/geom',
    columns: {},
    dimension: 2,
  },
  {
    human: 'Math/Trig Failures',
    key: 'MATH_TRIG',
    subject: 'math/trig',
    columns: {},
    dimension: 2,
  },
  {
    human: 'Global Failures',
    key: 'SS_GLOBAL',
    subject: 'global',
    columns: {},
    dimension: 2,
  },
  {
    human: 'US Failures',
    key: 'SS_US',
    subject: 'US',
    columns: {},
    dimension: 2,
  },
  {
    human: 'Econ Failures',
    key: 'SS_ECON',
    columns: {},
    dimension: 2,
  },
  {
    human: 'Govt Failures',
    key: 'SS_GOVT',
    subject: 'govt/econ',
    columns: {},
    dimension: 2,
  },
  {
    human: 'Govt/Econ Failures',
    key: 'SS_GOVT_ECON',
    columns: {},
    dimension: 2,
  },
  {
    human: 'Life Sci/Liv Failures',
    key: 'SCI_LIVING',
    subject: 'life sci/liv',
    columns: {},
    dimension: 2,
  },
  {
    human: 'Life Sci/Bio Failures',
    key: 'SCI_BIO',
    subject: 'life sci/bio',
    columns: {},
    dimension: 2,
  },
  {
    human: 'Phys Sci/Chem Failures',
    key: 'SCI_CHEM',
    subject: 'phys sci/chem',
    columns: {},
    dimension: 2,
  },
  {
    human: 'Phys Sci/Earth Failures',
    key: 'SCI_EARTH',
    subject: 'phys sci/earth',
    columns: {},
    dimension: 2,
  },
  {
    human: 'Phys Sci/Phys Failures',
    key: 'SCI_PHYSICS',
    subject: 'phys sci/phys',
    columns: {},
    dimension: 2,
  },
  {
    human: 'LOTE Failures',
    key: 'LOTE',
    subject: 'LOTE',
    columns: {},
    dimension: 2,
  },
  {
    human: 'PE Failures',
    key: 'PE',
    subject: 'PE',
    columns: {},
    dimension: 2,
  },
  {
    human: 'Health Failures',
    key: 'HEALTH',
    subject: 'health',
    columns: {},
    dimension: 2,
  },
  {
    human: 'Art Failures',
    key: 'ART',
    subject: 'art',
    columns: {},
    dimension: 2,
  },
  {
    human: 'CTE Failures',
    key: 'CTE',
    subject: 'CTE',
    columns: {},
    dimension: 2,
  },
];
